
export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export interface FAQCategory {
  id: string;
  name: string;
  description: string;
}

export const faqCategories: FAQCategory[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    description: "Basic information about using our platform"
  },
  {
    id: "image-generation",
    name: "Image Generation",
    description: "How to create and enhance images"
  },
  {
    id: "plans-billing",
    name: "Plans & Billing",
    description: "Subscription plans and payment information"
  },
  {
    id: "technical",
    name: "Technical",
    description: "Technical specifications and requirements"
  },
  {
    id: "account-support",
    name: "Account & Support",
    description: "Account management and customer support"
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "Business and enterprise solutions"
  },
  {
    id: "ai-models",
    name: "AI Models & Quality",
    description: "Questions about FLUX and other AI models"
  },
  {
    id: "ecommerce",
    name: "E-commerce & Business",
    description: "Using AI images for online selling"
  }
];

export const faqItems: FAQItem[] = [
  // Getting Started
  {
    id: "what-is-bulk-image-generator",
    question: "What is Bulk Image Generator?",
    answer: "Bulk Image Generator is an AI-powered platform that allows you to create multiple high-quality images at once using advanced AI models like FLUX.1 Pro. You can generate images for various purposes including marketing, social media, e-commerce, and creative projects. Generate up to 1000+ images per hour with professional quality.",
    category: "getting-started"
  },
  {
    id: "how-to-get-started",
    question: "How do I get started with image generation?",
    answer: "Simply sign up for an account (or try our free generator without signup), choose your preferred AI model, write a descriptive prompt, and click generate. You can start with our free plan to explore the platform's capabilities - no credit card required.",
    category: "getting-started"
  },
  {
    id: "available-ai-models",
    question: "What AI models are available?",
    answer: "We support FLUX.1 Schnell (fast, free tier), FLUX.1 Dev (high quality), FLUX.1 Pro (professional, highest quality), and other cutting-edge image generation models. Each model has its own strengths: Schnell for speed, Pro for photorealistic quality.",
    category: "getting-started"
  },
  {
    id: "tutorials-available",
    question: "Are there tutorials available to learn the platform?",
    answer: "Yes, we offer comprehensive video tutorials, written guides, and best practice documentation in our help center. We also have a prompt library with 500+ ready-to-use templates to help you master image generation techniques.",
    category: "getting-started"
  },
  {
    id: "free-trial",
    question: "Can I try the AI image generator for free?",
    answer: "Yes! We offer a completely free tier with 5 images per day - no credit card or signup required. Visit our Try Generator page to start creating AI images instantly. Paid plans start at just $7.99/month for more images and features.",
    category: "getting-started"
  },

  // Image Generation
  {
    id: "effective-prompts",
    question: "How do I write effective prompts for AI image generation?",
    answer: "Be specific and descriptive in your prompts. Include details about style, colors, composition, lighting, and mood. For example, instead of 'a dog', try 'a golden retriever sitting in a sunny garden, photorealistic style, warm lighting, 4K quality'. Our prompt enhancer can automatically improve your prompts for better results.",
    category: "image-generation"
  },
  {
    id: "prompt-enhancement",
    question: "How does the AI prompt enhancer work?",
    answer: "Our built-in prompt enhancement feature uses AI to automatically improve your prompts with additional descriptive elements, style keywords, and technical parameters. It can add lighting details, composition suggestions, and quality modifiers to achieve significantly better results.",
    category: "image-generation"
  },
  {
    id: "multiple-variations",
    question: "Can I generate multiple images with different variations?",
    answer: "Yes! You can generate 2-100 images at once with the same or varied prompts. Each generation produces unique results. This is perfect for creating product variations, A/B testing marketing images, or exploring creative possibilities.",
    category: "image-generation"
  },
  {
    id: "generation-time",
    question: "How long does it take to generate images?",
    answer: "Generation time varies by model: FLUX.1 Schnell takes 2-5 seconds per image, FLUX.1 Dev takes 10-20 seconds, and FLUX.1 Pro takes 15-30 seconds for the highest quality. Bulk generation of 100 images typically completes in 5-15 minutes.",
    category: "image-generation"
  },
  {
    id: "generation-failure",
    question: "What happens if my image generation fails?",
    answer: "If a generation fails, you won't be charged for that attempt. Common reasons for failure include content policy violations or temporary technical issues. Failed generations are automatically retried. You can try rephrasing your prompt or contact support for assistance.",
    category: "image-generation"
  },
  {
    id: "manage-generated-images",
    question: "How do I manage and organize my generated images?",
    answer: "All your generated images are stored in your personal gallery where you can view, download, organize into projects, and manage them. You can download individual images, bulk download as ZIP, or share images with others using our sharing feature.",
    category: "image-generation"
  },
  {
    id: "image-quality-settings",
    question: "What image quality and resolution options are available?",
    answer: "We support multiple resolutions: 512x512, 768x768, 1024x1024, and custom aspect ratios including 16:9 and 9:16 for social media. FLUX.1 Pro can generate up to 2048x2048 images. Our image enhancer can upscale to 4K resolution for print-ready files.",
    category: "image-generation"
  },

  // AI Models & Quality
  {
    id: "flux-vs-others",
    question: "How does FLUX compare to Midjourney or DALL-E?",
    answer: "FLUX.1 Pro offers comparable or superior quality to Midjourney v6 for photorealistic images, especially for product photography and commercial use. Unlike Midjourney, FLUX allows bulk generation (100+ images at once) and offers more flexible commercial licensing. FLUX is often 2-3x faster than competitors.",
    category: "ai-models"
  },
  {
    id: "best-model-for-products",
    question: "Which AI model is best for product photography?",
    answer: "For product photography, we recommend FLUX.1 Pro for its photorealistic quality and accurate rendering of products, textures, and lighting. It excels at white background shots, lifestyle images, and product mockups. FLUX.1 Dev is a good budget alternative with slightly lower quality.",
    category: "ai-models"
  },
  {
    id: "ai-image-quality-2026",
    question: "How has AI image quality improved in 2026?",
    answer: "AI image generation has made massive strides in 2026. FLUX.1 Pro now produces images virtually indistinguishable from professional photography for many use cases. Key improvements include better text rendering, more accurate hands/fingers, consistent brand colors, and photorealistic lighting.",
    category: "ai-models"
  },
  {
    id: "flux-schnell-limitations",
    question: "What are the limitations of FLUX.1 Schnell (free model)?",
    answer: "FLUX.1 Schnell is optimized for speed, generating images in 2-5 seconds. However, it has lower detail than Pro, may produce less accurate text, and works best for simpler compositions. It's perfect for quick iterations and testing prompts before using Pro credits.",
    category: "ai-models"
  },

  // E-commerce & Business
  {
    id: "etsy-product-photos",
    question: "Can I use AI-generated images for my Etsy shop?",
    answer: "Yes! Many successful Etsy sellers use our AI-generated images. Create product mockups, lifestyle shots, and listing images at scale. Our images are optimized for Etsy's image requirements. Pro tip: Generate multiple variations to A/B test which images get more clicks.",
    category: "ecommerce"
  },
  {
    id: "amazon-fba-photos",
    question: "Are AI images compliant with Amazon product photo requirements?",
    answer: "Yes, our AI can generate Amazon-compliant images including pure white background main images, infographic images, and lifestyle shots. We recommend using FLUX.1 Pro for the highest quality that meets Amazon's strict image standards for FBA listings.",
    category: "ecommerce"
  },
  {
    id: "shopify-store-images",
    question: "How can I use AI images for my Shopify store?",
    answer: "Generate product photos, hero banners, collection images, and marketing visuals for your Shopify store. Our bulk generation feature lets you create consistent imagery across your entire catalog quickly. Many users report 50-70% time savings compared to traditional photography.",
    category: "ecommerce"
  },
  {
    id: "dropshipping-product-images",
    question: "Can I create product images for dropshipping with AI?",
    answer: "Absolutely! AI image generation is perfect for dropshipping since you often don't have physical products to photograph. Create professional product images, lifestyle shots, and mockups for your store. Generate hundreds of product images in hours instead of days.",
    category: "ecommerce"
  },
  {
    id: "marketing-ad-images",
    question: "How do I create Facebook/Google Ads images with AI?",
    answer: "Use our marketing-optimized prompts to generate scroll-stopping ad creatives. Create multiple variations for A/B testing. Our images work great for Facebook Ads, Google Ads, Instagram, and TikTok. Generate dozens of ad variations in minutes instead of hours with a designer.",
    category: "ecommerce"
  },

  // Technical
  {
    id: "image-sizes-formats",
    question: "What image sizes and formats are supported?",
    answer: "We support various sizes including 512x512, 768x768, 1024x1024, 1024x768 (landscape), 768x1024 (portrait), and custom dimensions up to 2048x2048. Common aspect ratios like 1:1, 16:9, 9:16, and 4:3 are available. Output formats include PNG and JPEG.",
    category: "technical"
  },
  {
    id: "download-formats",
    question: "What file formats can I download?",
    answer: "Generated images are available in high-quality PNG format by default. You can also download as JPEG for smaller file sizes. Bulk downloads are provided as ZIP files. Our image enhancer can output print-ready files with custom DPI settings.",
    category: "technical"
  },
  {
    id: "edit-generated-images",
    question: "Can I edit or modify generated images after creation?",
    answer: "Yes! Our Image Tools Hub includes background removal, image enhancement, upscaling, format conversion, and more. You can also download images and edit them in external software like Photoshop. We're adding in-painting and editing features soon.",
    category: "technical"
  },
  {
    id: "api-access",
    question: "Do you offer API access for developers?",
    answer: "Yes, we provide REST API access for developers who want to integrate our image generation capabilities into their own applications. API documentation, code examples in multiple languages, and dedicated support are available for Professional and Enterprise plans.",
    category: "technical"
  },
  {
    id: "data-security",
    question: "Is my data and generated content secure?",
    answer: "We take security seriously and use industry-standard encryption (AES-256) to protect your data. Your generated images and personal information are stored securely and are only accessible to you. We do not use your images to train AI models. GDPR compliant.",
    category: "technical"
  },
  {
    id: "bulk-upload-csv",
    question: "Can I upload prompts in bulk via CSV file?",
    answer: "Yes! Upload a CSV file with hundreds of prompts to generate images in bulk. Perfect for creating large product catalogs or marketing campaigns. Each row becomes a separate generation job. We support CSV, TXT, and direct paste for bulk prompts.",
    category: "technical"
  },

  // Plans & Billing
  {
    id: "generation-limits",
    question: "Is there a limit to how many images I can generate?",
    answer: "Generation limits depend on your plan: Free (5/day), Hobby ($7.99/mo - 100 images), Creator ($19.99/mo - 500 images), Professional ($49.99/mo - 2000 images). Enterprise plans offer custom limits. Unused credits don't roll over.",
    category: "plans-billing"
  },
  {
    id: "upgrade-subscription",
    question: "How do I upgrade or change my subscription plan?",
    answer: "Upgrade anytime from your account settings or dashboard. Changes take effect immediately and you'll have instant access to the new plan's features and limits. We prorate charges for mid-cycle upgrades.",
    category: "plans-billing"
  },
  {
    id: "cancel-subscription",
    question: "Can I cancel my subscription anytime?",
    answer: "Yes, cancel anytime from your account settings with no cancellation fees. Your plan remains active until the end of your current billing period. You keep access to all previously generated images.",
    category: "plans-billing"
  },
  {
    id: "payment-methods",
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, Mastercard, Amex), debit cards, and PayPal. All payments are processed securely through Stripe with PCI-DSS compliance. We offer monthly and annual billing (save 20% with annual).",
    category: "plans-billing"
  },
  {
    id: "refunds",
    question: "Do you offer refunds?",
    answer: "We offer a 7-day money-back guarantee for first-time subscribers if you're not satisfied. Contact support within 7 days of your first paid subscription for a full refund. No questions asked.",
    category: "plans-billing"
  },
  {
    id: "commercial-use",
    question: "Can I use generated images commercially?",
    answer: "Yes! All images generated on paid plans can be used for commercial purposes including selling products, marketing, client work, and print-on-demand. You own full rights to your generated images. Free tier images also allow commercial use with attribution optional.",
    category: "plans-billing"
  },
  {
    id: "pricing-comparison",
    question: "How does your pricing compare to Midjourney or DALL-E?",
    answer: "We offer competitive pricing starting at $7.99/month for 100 images. Midjourney starts at $10/month with generation limits. Unlike competitors, we allow bulk generation of 100+ images at once, making us more efficient for commercial use. Our per-image cost can be as low as $0.02.",
    category: "plans-billing"
  },

  // Account & Support
  {
    id: "contact-support",
    question: "How do I contact customer support?",
    answer: "Reach our support team through the contact form, email (support@aigeneratedbulkimages.com), or live chat. We typically respond within 24 hours during business days. Pro and Enterprise customers get priority support with faster response times.",
    category: "account-support"
  },
  {
    id: "report-issues",
    question: "How do I report issues or request features?",
    answer: "If you encounter bugs or have feature requests, use our feedback form or contact support. We actively incorporate user feedback into our roadmap. Join our community forum to vote on upcoming features and connect with other users.",
    category: "account-support"
  },
  {
    id: "stay-updated",
    question: "How do I stay updated on new features and models?",
    answer: "Follow our blog for detailed updates, subscribe to our email newsletter for announcements, or join our community forum. We release new features and model improvements regularly - typically 2-4 major updates per month.",
    category: "account-support"
  },
  {
    id: "request-features",
    question: "Can I request specific AI models or features?",
    answer: "Absolutely! We value user feedback and prioritize popular feature requests. Submit suggestions through our feedback form. Recent user-requested features include background removal, image upscaling, and batch processing - all now available.",
    category: "account-support"
  },
  {
    id: "content-policies",
    question: "What content policies apply to image generation?",
    answer: "We prohibit generating inappropriate, harmful, illegal, or copyrighted content. Our AI automatically filters requests that violate policies. Repeated violations may result in account suspension. See our Terms of Service for full content guidelines.",
    category: "account-support"
  },

  // Enterprise
  {
    id: "enterprise-solutions",
    question: "Do you offer enterprise solutions for large teams?",
    answer: "Yes, we offer enterprise plans with custom image limits, dedicated API access, priority queue, SLA guarantees, dedicated account manager, custom model training, and white-label solutions. Contact sales for a personalized quote.",
    category: "enterprise"
  },
  {
    id: "team-collaboration",
    question: "Can I collaborate with team members on projects?",
    answer: "Team collaboration features are available in Professional and Enterprise plans. Share projects, manage permissions, create shared prompt libraries, and track team usage. Perfect for marketing teams and agencies.",
    category: "enterprise"
  },
  {
    id: "white-label",
    question: "Do you offer white-label solutions for agencies?",
    answer: "Yes, our Enterprise plan includes white-label options. Agencies can offer AI image generation under their own brand, with custom domains, branded interfaces, and flexible billing. Contact our enterprise sales team for details.",
    category: "enterprise"
  },
  {
    id: "api-rate-limits",
    question: "What are the API rate limits for enterprise use?",
    answer: "Enterprise API plans offer high-throughput access with customizable rate limits. Standard enterprise starts at 1000 requests/minute with options for dedicated infrastructure. We can accommodate requirements up to millions of images per month.",
    category: "enterprise"
  }
];
